/* ========================================
   NUCLEAR GRELLA - GLOBAL CONTROLLER
   World-Class JS Architecture
   ======================================== */

// 0. CENTRALIZED CONFIGURATION (Single Source of Truth)
window.nuclear = window.nuclear || {};

const NUCLEAR_CONFIG = Object.freeze({
    // Layout
    HEADER_HEIGHT: 56,
    PAGE_MAX_WIDTH: 480, // Mobile-Universe: locked to mobile width
    FREE_SHIPPING_THRESHOLD: 50000, // 500 INR in cents

    // Mobile-Universe: No breakpoints needed - single mobile view for all screens

    // Colors (match CSS custom properties)
    COLORS: Object.freeze({
        GOLD: '#FDB91A',
        GOLD_HOVER: '#e5a617',
        NAVY: '#191d38',
        NAVY_LIGHT: '#232848',
        TEXT_PRIMARY: '#ffffff',
        TEXT_MUTED: 'rgba(255, 255, 255, 0.7)'
    }),

    // Animation
    ANIMATION: Object.freeze({
        FAST: 150,
        NORMAL: 300,
        SLOW: 500,
        EASING: 'cubic-bezier(0.16, 1, 0.3, 1)'
    }),

    // Timing
    TIMING: Object.freeze({
        TOAST_DURATION: 3000,
        DEBOUNCE_DELAY: 250,
        CART_SYNC_DELAY: 100
    }),

    // API Routes (populated by Liquid)
    ROUTES: {
        root: '/',
        cart: '/cart',
        cartAdd: '/cart/add.js',
        cartChange: '/cart/change.js',
        cartGet: '/cart.js'
    }
});

// Expose globally
window.NUCLEAR_CONFIG = NUCLEAR_CONFIG;

// 0.5 GLOBAL UTILITIES (DRY - Don't Repeat Yourself)
const NuclearUtils = {
    // DOM Helpers
    $(selector) { return document.querySelector(selector); },
    $$(selector) { return Array.from(document.querySelectorAll(selector)); },

    // Safe class operations
    addClass(el, ...classes) { el?.classList.add(...classes); },
    removeClass(el, ...classes) { el?.classList.remove(...classes); },
    toggleClass(el, cls, force) { return el?.classList.toggle(cls, force); },
    hasClass(el, cls) { return el?.classList.contains(cls); },

    // Mobile-Universe: No responsive helpers needed - always mobile view
    isMobile() { return true; },
    isTablet() { return false; },
    isDesktop() { return false; },

    // Debounce (prevent excessive calls)
    debounce(fn, delay = NUCLEAR_CONFIG.TIMING.DEBOUNCE_DELAY) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(this, args), delay);
        };
    },

    // Throttle (limit call frequency)
    throttle(fn, limit = 100) {
        let inThrottle;
        return (...args) => {
            if (!inThrottle) {
                fn.apply(this, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    },

    // Safe JSON parse
    parseJSON(str, fallback = null) {
        try { return JSON.parse(str); }
        catch { return fallback; }
    },

    // Format money for India storefront display
    formatMoney(cents) {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format((cents || 0) / 100);
    },

    // Animate element
    animate(el, keyframes, options = {}) {
        const defaults = { duration: NUCLEAR_CONFIG.ANIMATION.NORMAL, easing: NUCLEAR_CONFIG.ANIMATION.EASING };
        return el?.animate(keyframes, { ...defaults, ...options });
    },

    // Fade helpers
    fadeIn(el, duration = NUCLEAR_CONFIG.ANIMATION.NORMAL) {
        if (!el) return;
        el.style.display = '';
        return this.animate(el, [{ opacity: 0 }, { opacity: 1 }], { duration });
    },
    fadeOut(el, duration = NUCLEAR_CONFIG.ANIMATION.NORMAL) {
        if (!el) return;
        const anim = this.animate(el, [{ opacity: 1 }, { opacity: 0 }], { duration });
        anim?.finished.then(() => el.style.display = 'none');
        return anim;
    },

    // Scroll helpers
    scrollToTop(smooth = true) {
        window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
    },
    scrollToElement(el, offset = NUCLEAR_CONFIG.HEADER_HEIGHT) {
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
    }
};

// Expose globally
window.NuclearUtils = NuclearUtils;
window.nuclear.utils = NuclearUtils;

// 1. EVENT BUS (Central Nervous System)
class EventBus {
    constructor() {
        this.events = {};
    }

    on(event, callback) {
        if (!this.events[event]) this.events[event] = [];
        this.events[event].push(callback);
        return () => this.off(event, callback); // Return unsubscribe
    }

    off(event, callback) {
        if (!this.events[event]) return;
        this.events[event] = this.events[event].filter(cb => cb !== callback);
    }

    emit(event, data = {}) {
        if (this.events[event]) {
            this.events[event].forEach(cb => {
                try {
                    cb(data);
                } catch (e) {
                    console.error(`Error in EventBus listener for ${event}:`, e);
                }
            });
        }
    }
}
window.nuclear.events = new EventBus();

// 2. TOAST NOTIFICATIONS
const Toast = {
    container: null,

    init() {
        this.container = document.getElementById('toast-container');
        if (!this.container) {
            // Create if missing
            this.container = document.createElement('div');
            this.container.id = 'toast-container';
            document.body.appendChild(this.container);
        }
    },

    show(message, type, duration = 3000) {
        if (!this.container) this.init();

        const icons = { success: '✓', error: '✕', info: 'ℹ', warning: '⚠' };

        // Build with textContent (not innerHTML) so an attacker-controlled
        // `message` string can't inject HTML into the toast container.
        // Some callers pass server-derived strings (e.g. error messages
        // proxied from the cart API) so this matters.
        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;
        const iconEl = document.createElement('span');
        iconEl.className = 'toast-icon';
        iconEl.textContent = icons[type] || '';
        const msgEl = document.createElement('span');
        msgEl.className = 'toast-message';
        msgEl.textContent = message;
        toast.appendChild(iconEl);
        toast.appendChild(msgEl);

        this.container.appendChild(toast);

        // Auto remove
        setTimeout(() => {
            toast.classList.add('hiding');
            setTimeout(() => {
                if (toast.parentNode) toast.parentNode.removeChild(toast);
            }, 300);
        }, duration);
    },

    success(msg, dur) { this.show(msg, 'success', dur); },
    error(msg, dur) { this.show(msg, 'error', dur); },
    info(msg, dur) { this.show(msg, 'info', dur); },
    warning(msg, dur) { this.show(msg, 'warning', dur); }
};

// 2.5 CART TOAST PREVIEW (Rich Add-to-Cart Confirmation)
const CartToast = {
    element: null,
    hideTimeout: null,

    init() {
        // Create the toast element if it doesn't exist
        if (!document.getElementById('cart-toast')) {
            const toast = document.createElement('div');
            toast.id = 'cart-toast';
            toast.className = 'cart-toast';
            toast.innerHTML = `
                <button type="button" class="cart-toast-close" aria-label="Close">&times;</button>
                <div class="cart-toast-content">
                    <img class="cart-toast-image" src="" alt="Product">
                    <div class="cart-toast-info">
                        <h4 class="cart-toast-title"></h4>
                        <p class="cart-toast-message">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>${window.nuclear.strings?.added || 'Added to cart'}</span>
                        </p>
                        <a href="/cart" class="cart-toast-link">View Cart &rarr;</a>
                    </div>
                </div>
                <div class="cart-toast-actions">
                    <a href="/cart" class="cart-toast-btn cart-toast-btn-primary">${window.nuclear.strings?.viewCart || 'View Cart'}</a>
                    <button type="button" class="cart-toast-btn cart-toast-btn-secondary" data-action="close-cart-toast">${window.nuclear.strings?.continueShopping || 'Continue Shopping'}</button>
                </div>
            `;
            document.body.appendChild(toast);
            this.element = toast;

            // Close button handler
            toast.querySelector('.cart-toast-close').addEventListener('click', () => this.hide());
            toast.querySelector('[data-action="close-cart-toast"]').addEventListener('click', () => this.hide());
        } else {
            this.element = document.getElementById('cart-toast');
        }
    },

    show(productData = {}) {
        if (!this.element) this.init();

        // Clear any pending hide timeout
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }

        // Update content
        const img = this.element.querySelector('.cart-toast-image');
        const title = this.element.querySelector('.cart-toast-title');

        if (productData.image) {
            img.src = productData.image;
            img.alt = productData.title || 'Product';
        }

        if (productData.title) {
            title.textContent = productData.title;
        }

        // Show the toast
        requestAnimationFrame(() => {
            this.element.classList.add('visible');
        });

        // Auto-hide after 5 seconds
        this.hideTimeout = setTimeout(() => {
            this.hide();
        }, 5000);

        // Haptic feedback (if supported)
        if ('vibrate' in navigator) {
            navigator.vibrate(50);
        }
    },

    hide() {
        if (this.element) {
            this.element.classList.remove('visible');
        }
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
    }
};

// Expose globally
// Utility: Generate UUID for Idempotency
function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

// Utility: Fetch with Timeout
function fetchWithTimeout(resource, options = {}) {
    const { timeout = 10000 } = options;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    return fetch(resource, {
        ...options,
        signal: controller.signal
    }).finally(() => clearTimeout(id));
}

// Expose globally
window.CartToast = CartToast;
if (!window.nuclear.ui) window.nuclear.ui = {};
window.nuclear.ui.CartToast = CartToast;

const CartManager = {
    queue: Promise.resolve(),
    cart: null, // Cache cart state

    normalizeProperties(properties = {}) {
        const normalized = {};
        if (!properties || typeof properties !== 'object') return normalized;

        Object.entries(properties).forEach(([key, value]) => {
            if (value == null) return;
            const cleanKey = String(key).trim();
            const cleanValue = String(value).trim();
            if (!cleanKey || !cleanValue) return;
            normalized[cleanKey] = cleanValue;
        });

        return normalized;
    },

    propertiesMatch(itemProperties = {}, expectedProperties = {}) {
        const normalizedItem = this.normalizeProperties(itemProperties);
        const normalizedExpected = this.normalizeProperties(expectedProperties);
        const itemKeys = Object.keys(normalizedItem);
        const expectedKeys = Object.keys(normalizedExpected);

        if (expectedKeys.length === 0) return true;
        if (itemKeys.length !== expectedKeys.length) return false;

        return expectedKeys.every((key) => normalizedItem[key] === normalizedExpected[key]);
    },

    emitCartUpdated(cart) {
        this.cart = cart;
        this.updateBadge(cart?.item_count || 0);
        window.nuclear.events.emit('CART_UPDATED', cart);
        window.nuclear.events.emit('cart_updated', cart);
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    },

    // Get optimistic count
    getCount() {
        if (this.cart) return this.cart.item_count;
        const badge = document.querySelector('.cart-badge');
        return badge ? parseInt(badge.innerText, 10) || 0 : 0;
    },

    getItem(identifier, options = {}) {
        const cart = options.cart || this.cart;
        if (!cart || !Array.isArray(cart.items) || identifier == null) return null;
        const id = String(identifier);
        const expectedProperties = this.normalizeProperties(options.properties);
        return cart.items.find(i => String(i.key) === id)
            || cart.items.find(i => String(i.variant_id) === id && this.propertiesMatch(i.properties, expectedProperties))
            || (!Object.keys(expectedProperties).length
                ? cart.items.find(i => String(i.variant_id) === id)
                : null)
            || null;
    },

    // Get quantity of specific line item or variant
    getItemQty(identifier, options = {}) {
        const item = this.getItem(identifier, options);
        return item ? item.quantity : 0;
    },

    getItemKey(identifier, options = {}) {
        const item = this.getItem(identifier, options);
        return item ? item.key : null;
    },

    getVariantQty(variantId) {
        if (!this.cart || !Array.isArray(this.cart.items) || variantId == null) return 0;
        const id = String(variantId);
        return this.cart.items.reduce((sum, item) => {
            return String(item.variant_id) === id ? sum + (parseInt(item.quantity, 10) || 0) : sum;
        }, 0);
    },

    updateBadge(count) {
        // Broadcast for other listeners
        window.nuclear.events.emit('CART_COUNT_UPDATED', count);

        // Header script defines its own updater. If present, use ONLY that
        // one — calling it AND then re-writing all .cart-badge nodes here
        // causes a visible double-flicker on every cart change.
        if (typeof window.updateHeaderCartCount === 'function') {
            window.updateHeaderCartCount(count);
            return;
        }

        document.querySelectorAll('.cart-badge').forEach(badge => {
            badge.innerText = count;
            badge.style.display = count > 0 ? 'flex' : 'none';
        });
    },

    // Enqueue actions to prevent race conditions
    enqueue(action) {
        const run = this.queue
            .catch(err => {
                console.error('Cart Queue Error:', err);
            })
            .then(() => action());

        this.queue = run.catch(err => {
            console.error('Cart Queue Error:', err);
        });

        return run;
    },

    fetchCartState(options = {}) {
        const shouldEmit = options.emit !== false;

        return fetchWithTimeout(window.Shopify.routes.root + 'cart.js')
            .then(r => {
                if (!r.ok) throw new Error('Failed to sync cart');
                return r.json();
            })
            .then(cart => {
                if (shouldEmit) {
                    this.emitCartUpdated(cart);
                }
                return cart;
            });
    },

    scheduleSync() {
        setTimeout(() => {
            this.sync().catch(() => { });
        }, 0);
    },

    sync() {
        return this.enqueue(() => this.fetchCartState());
    },

    addItem(variantId, qty = 1, opts = {}) {
        // #109 Variant Validation
        if (!variantId || variantId === 'undefined' || variantId === 'null') {
            Toast.error('Invalid product');
            if (opts.onError) opts.onError(new Error('Invalid variant ID'));
            // Error already surfaced (Toast.error + onError); resolve so the
            // caller's fire-and-forget promise doesn't reject unhandled.
            return Promise.resolve(null);
        }

        // INSTANT: Update UI immediately (before queue)
        const current = this.getCount();
        this.updateBadge(current + qty);
        if (opts.onStart) opts.onStart();

        return this.enqueue(() => {
            // #104 Idempotency Header
            const headers = {
                'Content-Type': 'application/json',
                'X-Request-ID': generateUUID()
            };
            const itemPayload = {
                id: String(variantId),
                quantity: qty
            };
            const normalizedProperties = this.normalizeProperties(opts.properties);
            if (Object.keys(normalizedProperties).length) {
                itemPayload.properties = normalizedProperties;
            }
            if (opts.sellingPlan) {
                itemPayload.selling_plan = opts.sellingPlan;
            }

            return fetchWithTimeout(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ items: [itemPayload] })
            })
                .then(r => {
                    if (!r.ok) throw new Error('Network response was not ok');
                    return r.json();
                })
                .then(data => {
                    const addedItem = Array.isArray(data?.items)
                        ? data.items[0]
                        : (Array.isArray(data) ? data[0] : data);

                    CartToast.hide();
                    window.nuclear.events.emit('item_added', addedItem || data);

                    // Fetch fresh cart so the cart:updated listeners are the
                    // single source of UI truth. Callers fire their own toasts
                    // in onSuccess AFTER this resolves, avoiding the
                    // "Added → reverts → flickers to Added" race.
                    return this.fetchCartState()
                        .catch(syncError => {
                            console.error('Cart refresh after add failed:', syncError);
                            this.scheduleSync();
                            return this.cart;
                        })
                        .then(cart => {
                            if (opts.onSuccess) opts.onSuccess(addedItem || data, cart);
                            return addedItem || data;
                        });
                })
                .catch(e => {
                    console.error('Add Item Error:', e);
                    // Don't fire a toast here — the caller's onError owns the
                    // user-visible error message so we never double-toast.
                    // #97: Sync from server to get true cart state.
                    this.scheduleSync();
                    if (opts.onError) opts.onError(e);
                    // Error is fully handled here (onError = the caller's error
                    // channel) + scheduleSync above. These calls are fire-and-
                    // forget — nothing attaches .catch to the returned promise —
                    // so re-throwing produced an "Uncaught (in promise)" on
                    // every failure. Resolve instead. (enqueue()'s queue tail
                    // already swallows its own copy, so the queue keeps running.)
                    return null;
                });
        });
    },

    changeItem(identifier, quantity, opts = {}) {
        // #109 Variant Validation
        // Resolve (not reject) on bad input — callers don't .catch, so a
        // rejection here would surface as an unhandled promise rejection.
        if (!identifier) return Promise.resolve(null);

        // INSTANT: Update UI immediately
        if (opts.onStart) opts.onStart();

        return this.enqueue(() => {
            // #104 Idempotency Header
            const headers = {
                'Content-Type': 'application/json',
                'X-Request-ID': generateUUID()
            };
            const resolvedIdentifier = opts.lineKey
                || this.getItemKey(identifier, { properties: opts.properties })
                || identifier;

            return fetchWithTimeout(window.Shopify.routes.root + 'cart/change.js', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ id: String(resolvedIdentifier), quantity: quantity })
            })
                .then(r => {
                    // #113 Error Handling for non-200
                    if (!r.ok) throw new Error('Network response was not ok');
                    return r.json();
                })
                .then(cart => {
                    this.emitCartUpdated(cart);
                    // Toast is the caller's responsibility — keeps qty-pill
                    // spam-clicks from spawning a flood of "Cart Updated"
                    // toasts and lets each call site pick its own message.
                    if (opts.onSuccess) opts.onSuccess(cart);
                    return cart;
                })
                .catch(e => {
                    console.error('Change Item Error:', e);
                    // Don't toast here — caller's onError owns the message.
                    this.scheduleSync();
                    if (opts.onError) opts.onError(e);
                    // Handled via onError + scheduleSync; resolve rather than
                    // re-throw so this fire-and-forget promise doesn't surface
                    // an unhandled rejection. See addItem's catch for details.
                    return null;
                });
        });
    },

    removeItem(identifier, opts = {}) {
        return this.changeItem(identifier, 0, opts);
    }
};
if (!window.nuclear.utils) window.nuclear.utils = {};
window.nuclear.utils.CartManager = CartManager;

// 4. WISHLIST MANAGER
const WishlistManager = {
    STORAGE_KEY: 'grella_wishlist',

    init() {
        this.syncUI();
        // Global delegation for dynamic buttons
        document.addEventListener('click', e => {
            const btn = e.target.closest('[data-wishlist-btn]');
            if (btn) {
                e.preventDefault();
                this.toggle(btn.dataset.productId);
            }
        });
    },

    getAll() {
        try { return JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || []; }
        catch { return []; }
    },

    has(id) { return this.getAll().includes(String(id)); },

    toggle(id) {
        id = String(id);
        let list = this.getAll();
        const exists = list.includes(id);

        if (exists) {
            list = list.filter(x => x !== id);
            Toast.warning('Removed from favorites');
        } else {
            list.push(id);
            Toast.success('Added to favorites!');
        }

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(list));
        this.syncUI();
        window.nuclear.events.emit('wishlist_updated', list);
    },

    // #116 Fix: Missing remove method alias
    remove(id) {
        id = String(id);
        let list = this.getAll();
        if (list.includes(id)) {
            list = list.filter(x => x !== id);
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(list));
            this.syncUI();
            window.nuclear.events.emit('wishlist_updated', list);
            Toast.warning('Removed from favorites');
        }
    },

    syncUI() {
        const list = this.getAll();
        document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
            const id = btn.dataset.productId;
            if (list.includes(String(id))) btn.classList.add('active');
            else btn.classList.remove('active');
        });

        // Update any header counters
        if (window.updateHeaderWishlistCount) window.updateHeaderWishlistCount(list.length);
    }
};

// 5. MODAL MANAGER
const ModalManager = {
    modals: {},
    active: null,
    backdrop: null,
    lastFocus: null,

    init() {
        this.backdrop = document.getElementById('globalBackdrop');
        if (this.backdrop) {
            this.backdrop.addEventListener('click', () => this.closeAll());
        }
        document.addEventListener('keydown', e => {
            if (e.key === 'Escape') this.closeAll();
            if (e.key === 'Tab' && this.active) {
                const config = this.modals[this.active];
                const el = document.querySelector(config.element);
                if (!el) return;

                const focusable = el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                if (!focusable.length) return;

                const first = focusable[0];
                const last = focusable[focusable.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === first) {
                        e.preventDefault();
                        last.focus();
                    }
                } else {
                    if (document.activeElement === last) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }
        });
    },

    register(id, config) { this.modals[id] = config; },

    shouldUseBackdrop(config) {
        return !!config && config.backdrop !== false && config.useBackdrop !== false;
    },

    open(id) {
        const config = this.modals[id];
        if (!config) return;
        const el = document.querySelector(config.element);
        if (!el) return;

        this.closeAll(); // Close others first

        el.classList.add('modal-open');
        el.classList.add('visible'); // Consistent class
        el.setAttribute('aria-hidden', 'false');
        if (this.backdrop && this.shouldUseBackdrop(config)) this.backdrop.classList.add('visible');

        this.active = id;
        if (config.onOpen) config.onOpen(el);
        document.body.style.overflow = 'hidden';

        // Save focus and trap. The actual Tab-key handling is done by the
        // global keydown listener attached in ModalManager.init() — we
        // used to build a per-modal handler here too but it was never
        // attached, just leaked as a property. Removed.
        this.lastFocus = document.activeElement;
        const focusable = el.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length) focusable[0].focus();
    },

    closeAll() {
        if (!this.active) return;
        const config = this.modals[this.active];
        const el = document.querySelector(config.element);

        if (el) {
            el.classList.remove('modal-open');
            el.classList.remove('visible');
            el.setAttribute('aria-hidden', 'true');
        }
        if (this.backdrop) this.backdrop.classList.remove('visible');

        if (config && config.onClose) config.onClose(el);
        this.active = null;
        document.body.style.overflow = '';

        // Restore focus
        if (this.lastFocus) {
            this.lastFocus.focus();
            this.lastFocus = null;
        }
    },

    // Close a specific modal by ID
    close(id) {
        if (!id || this.active !== id) {
            // If requested modal is not active, just return
            return;
        }
        this.closeAll();
    }
};

// 6. NOTIFY MANAGER
const NotifyManager = {
    init() {
        // Expose global for inline usage if needed, but prefer events
        window.NotifyManager = this;
        if (!window.nuclear.managers) window.nuclear.managers = {};
        window.nuclear.managers.NotifyManager = this;
    },
    openModal(productId, productName) {
        // Fill data
        const modal = document.getElementById('notifyModal');
        if (!modal) return;

        // Use ModalManager to handle open/close logic
        ModalManager.register('notify', { element: '#notifyModal' });

        document.getElementById('notifyProductId').value = productId;
        const nameEl = document.getElementById('notifyProductName');
        if (nameEl) nameEl.textContent = productName || "We'll email you when it's available";
        const emailEl = document.getElementById('notifyEmail');
        if (emailEl) emailEl.value = '';
        const timestampEl = document.getElementById('notifyTimestamp');
        if (timestampEl) timestampEl.value = String(Date.now());

        ModalManager.open('notify');
    },
    submit(e) {
        e.preventDefault();
        const emailEl = document.getElementById('notifyEmail');
        const productIdEl = document.getElementById('notifyProductId');
        const productNameEl = document.getElementById('notifyProductName');
        const submitBtn = e.target?.querySelector('button[type="submit"], .btn-submit-notify');

        const email = (emailEl?.value || '').trim();
        const productId = (productIdEl?.value || '').trim();
        const productName = (productNameEl?.textContent || '').trim();

        // Minimal validation — Shopify's /contact endpoint will reject
        // malformed addresses anyway, but failing fast avoids a round-trip.
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!emailOk) return Toast.error('Please enter a valid email');

        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.dataset._originalText = submitBtn.textContent;
            submitBtn.textContent = 'Submitting...';
        }

        // ─────────────────────────────────────────────────────────────
        // NOTE — this is a CONTACT-FORM-BASED capture, not a true
        // back-in-stock app. Submissions land in Shopify Admin → Apps
        // → Email/Notifications and as an email to the store owner.
        // Re-stocking the product does NOT automatically email these
        // customers; the merchant has to manually send when inventory
        // returns.
        //
        // To make this fully automatic, install a back-in-stock app
        // (Klaviyo Back in Stock, Back in Stock by SwiftReply,
        // PushOwl, etc.) and replace this fetch() with the app's API.
        // ─────────────────────────────────────────────────────────────

        // Session-level idempotency: prevent the same (product, email)
        // pair from being submitted twice in the same browser session.
        const idempKey = 'notify:' + productId + ':' + email.toLowerCase();
        try {
            if (sessionStorage.getItem(idempKey)) {
                Toast.success("You're already on the list for this one.");
                ModalManager.closeAll();
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.textContent = submitBtn.dataset._originalText || 'Submit';
                    delete submitBtn.dataset._originalText;
                }
                return;
            }
        } catch (_) { /* sessionStorage disabled (private mode) — proceed anyway */ }

        const body = new URLSearchParams();
        body.set('form_type', 'contact');
        body.set('utf8', '✓');
        body.set('contact[email]', email);
        body.set('contact[subject]', `Back-in-stock notify: ${productName || productId}`);
        body.set('contact[body]', `Customer requested a notification when this product is back in stock.\n\nProduct: ${productName || '(unnamed)'}\nProduct ID: ${productId || '(unknown)'}\nEmail: ${email}\nSubmitted: ${new Date().toISOString()}`);

        fetch('/contact#contact_form', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'text/html' },
            body: body.toString(),
            credentials: 'same-origin'
        }).then((res) => {
            // Shopify redirects to /contact?contact_posted=true on success.
            // Either a 200 or a 302-with-the-flag counts as success — we
            // can't read cross-origin redirect URLs after follow, so we
            // treat any non-error response as success.
            if (!res.ok && res.status !== 0) throw new Error('Contact form rejected: ' + res.status);
            try { sessionStorage.setItem(idempKey, '1'); } catch (_) {}
            // Honest copy: we logged the request, but the actual notify
            // happens manually from the merchant side (see comment above).
            Toast.success("Thanks — we've logged your request.");
            ModalManager.closeAll();
        }).catch((err) => {
            console.error('Notify submit failed:', err);
            Toast.error("Couldn't submit. Please try again.");
        }).finally(() => {
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = submitBtn.dataset._originalText || 'Submit';
                delete submitBtn.dataset._originalText;
            }
        });
    },
    closeModal() {
        ModalManager.closeAll();
    }
};


// 7. BUTTON STATE MACHINE (Prevents conflicting states)
const ButtonStates = Object.freeze({
    IDLE: 'idle',
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error'
});

const ButtonStateMachine = {
    setState(btn, state, customText = null) {
        if (!btn) return;

        const strings = window.nuclear?.strings || {};
        const textEl = btn.querySelector('[data-button-label], .btn-text, .cta-label, .sticky-action-label');

        // Capture the original idle text ONCE on first call and cache it on
        // the button. Otherwise, after one loading cycle the textContent
        // becomes "Adding..." and that becomes the new "idle" label forever.
        if (!btn.dataset.originalLabel) {
            const initial = (btn.dataset.idleText || textEl?.dataset.idleText || textEl?.textContent || btn.textContent || '').trim();
            btn.dataset.originalLabel = initial;
        }
        const idleSource = btn.dataset.originalLabel;

        const texts = {
            idle: idleSource || strings.addToCart || 'Add to Cart',
            loading: btn.dataset.loadingText || strings.adding || 'Adding...',
            success: btn.dataset.successText || strings.added || '✓ Added',
            error: btn.dataset.errorText || strings.error || 'Error'
        };

        // Remove all state classes
        Object.values(ButtonStates).forEach(s => btn.classList.remove(`btn--${s}`));

        // Add current state
        btn.classList.add(`btn--${state}`);
        btn.disabled = state === ButtonStates.LOADING;

        // Update text
        const labelTarget = textEl || btn;
        labelTarget.textContent = customText || texts[state];

        // Auto-reset success/error after delay. Cancel any prior reset
        // timer so rapid SUCCESS→ERROR transitions don't race to IDLE.
        if (btn._stateResetTimer) {
            clearTimeout(btn._stateResetTimer);
            btn._stateResetTimer = null;
        }
        if (state === ButtonStates.SUCCESS || state === ButtonStates.ERROR) {
            btn._stateResetTimer = setTimeout(() => {
                btn._stateResetTimer = null;
                this.setState(btn, ButtonStates.IDLE);
            }, NUCLEAR_CONFIG.TIMING.TOAST_DURATION);
        }
    },

    setLoading(btn) { this.setState(btn, ButtonStates.LOADING); },
    setSuccess(btn) { this.setState(btn, ButtonStates.SUCCESS); },
    setError(btn) { this.setState(btn, ButtonStates.ERROR); },
    setIdle(btn) { this.setState(btn, ButtonStates.IDLE); }
};

window.ButtonStateMachine = ButtonStateMachine;
window.nuclear.utils.ButtonStateMachine = ButtonStateMachine;

// 8. EVENT DELEGATION SYSTEM (Central Click Handler)
const ActionHandler = {
    actions: {},

    // Register an action
    register(actionName, handler) {
        this.actions[actionName] = handler;
    },

    // Initialize delegation
    init() {
        document.addEventListener('click', (e) => {
            const actionEl = e.target.closest('[data-action]');
            if (!actionEl) return;

            // #118 Fix: Check disabled state
            if (actionEl.disabled || actionEl.classList.contains('disabled') || actionEl.getAttribute('aria-disabled') === 'true') {
                e.preventDefault();
                return;
            }

            const actionName = actionEl.dataset.action;
            const handler = this.actions[actionName];

            if (handler) {
                e.preventDefault();
                handler(actionEl, e);
            }
        });

        // Listen for change events (selects, inputs)
        document.addEventListener('change', (e) => {
            const actionEl = e.target.closest('[data-action]');
            if (!actionEl) return;

            const actionName = actionEl.dataset.action;
            const handler = this.actions[actionName];

            if (handler) {
                handler(actionEl, e);
            }
        });

        // Register built-in actions
        this.registerBuiltInActions();

        // #117 Fix: Sold Out Default Check
        document.querySelectorAll('[data-max-inventory]').forEach(btn => {
            const max = parseInt(btn.dataset.maxInventory, 10);
            const variantId = btn.dataset.variantId;
            if (!isNaN(max) && max <= 0) {
                btn.disabled = true;
                btn.classList.add('disabled');
                const textEl = btn.querySelector('.btn-text');
                if (textEl) textEl.textContent = 'Sold Out';
            }
        });
    },

    registerBuiltInActions() {
        // Variant Changes (Price & ID update)
        this.register('select-variant', (el) => {
            const variantId = el.value;
            const option = el.options[el.selectedIndex];
            if (!option) return; // no option selected (selectedIndex -1) — nothing to sync
            const price = option.dataset.price;
            const maxInv = option.dataset.maxInventory; // Read from option

            // 1. Update Add to Cart buttons
            const container = el.closest('.product-info-column') || document;
            const addBtns = container.querySelectorAll('[data-action="add-to-cart"], [data-action="pdp-add"]');
            addBtns.forEach(btn => {
                btn.dataset.variantId = variantId;
                if (maxInv) btn.dataset.maxInventory = maxInv; // Update limit
            });

            // 2. Update Price
            if (price) {
                const priceEl = container.querySelector('.pdp-price');
                if (priceEl) priceEl.textContent = price;
            }

            if (typeof window.syncPdpPurchaseState === 'function') {
                window.syncPdpPurchaseState(variantId, option);
            }
        });

        // #117 Fix: Check availability on load for all buttons
        document.querySelectorAll('[data-max-inventory]').forEach(btn => {
            const max = parseInt(btn.dataset.maxInventory, 10);
            const variantId = btn.dataset.variantId;
            if (!isNaN(max) && window.CartManager) {
                const current = CartManager.getVariantQty(variantId);
                if (current >= max) {
                    btn.disabled = true;
                    btn.classList.add('sold-out');
                    const txt = btn.querySelector('.btn-text');
                    if (txt) txt.textContent = 'Limit Reached';
                }
            }
        });

        // Add to Cart
        this.register('add-to-cart', (el) => {
            const variantId = el.dataset.variantId;
            // Check if there is a linked quantity input
            const qtyInput = el.closest('.pdp-actions')?.querySelector('input[type="number"]');
            // Always default to 1 so an empty/cleared input doesn't send NaN
            // to the cart API (which returns 422 and breaks the click silently).
            const qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : (parseInt(el.dataset.quantity, 10) || 1);
            const cartOptions = typeof window.getSelectedPurchaseCartOptions === 'function'
                ? window.getSelectedPurchaseCartOptions()
                : {};

            if (!variantId) return;

            // #96 Fix: Overselling Protection
            const max = parseInt(el.dataset.maxInventory, 10);
            if (!isNaN(max)) {
                const currentInCart = CartManager.getVariantQty(variantId);
                if (currentInCart + qty > max) {
                    Toast.error(`Only ${max} available!`);
                    return;
                }
            }

            ButtonStateMachine.setLoading(el);
            CartManager.addItem(variantId, qty, {
                ...cartOptions,
                onSuccess: () => {
                    ButtonStateMachine.setSuccess(el);
                    Toast.success(window.nuclear.strings?.added || 'Added to cart!');
                },
                onError: () => {
                    ButtonStateMachine.setError(el);
                    Toast.error('Could not add to cart');
                }
            });
        });

        // PDP Local Quantity Update (No API call)
        this.register('pdp-update-qty', (el) => {
            const change = parseInt(el.dataset.change, 10) || 0;
            const wrapper = el.closest('.qty-wrapper');
            const input = wrapper?.querySelector('input[type="number"]');

            if (!input) return;

            let val = parseInt(input.value, 10) || 1;
            const max = parseInt(input.max, 10); // standard HTML attribute

            const newVal = val + change;
            if (!isNaN(max) && newVal > max) {
                Toast.error(`Max limit is ${max}`);
                return;
            }

            val = Math.max(1, newVal);
            input.value = val;
        });

        // Toggle Wishlist
        this.register('toggle-wishlist', (el) => {
            const productId = el.dataset.productId;
            if (productId) WishlistManager.toggle(productId);
        });

        // Open Notify Modal
        this.register('open-notify', (el) => {
            const productId = el.dataset.productId;
            const productName = el.dataset.productName;
            NotifyManager.openModal(productId, productName);
        });

        // Scroll to element
        this.register('scroll-to', (el) => {
            const target = document.querySelector(el.dataset.target);
            NuclearUtils.scrollToElement(target);
        });

        // Mobile Add (with flip to qty selector)
        this.register('mobile-add', (el) => {
            const variantId = el.dataset.variantId;
            const wrapper = el.closest('[data-qty-wrapper]');
            const qtySelector = wrapper?.querySelector('.mobile-qty-selector, .qty-selector');
            const qtyButtons = qtySelector?.querySelectorAll('[data-action="update-qty"], [data-action="collection-qty"]') || [];

            if (!variantId) return;

            // #96 Fix: Overselling Protection
            const max = parseInt(el.dataset.maxInventory, 10);
            if (!isNaN(max)) {
                const currentInCart = CartManager.getVariantQty(variantId);
                if (currentInCart + 1 > max) {
                    Toast.error(`Only ${max} available!`);
                    return;
                }
            }

            // Flip UI
            NuclearUtils.addClass(el, 'hidden');
            NuclearUtils.addClass(qtySelector, 'visible');

            ButtonStateMachine.setLoading(el);
            CartManager.addItem(variantId, 1, {
                onSuccess: (data, cart) => {
                    ButtonStateMachine.setSuccess(el);
                    Toast.success(window.nuclear.strings?.added || 'Added to cart!');
                    if (wrapper && data && data.key) {
                        wrapper.dataset.lineKey = data.key;
                    }
                    qtyButtons.forEach((btn) => {
                        if (data && data.key) btn.dataset.lineKey = data.key;
                    });
                    const qtyDisplay = wrapper?.querySelector('[data-qty-display]');
                    const lineItem = data && data.key && cart && Array.isArray(cart.items)
                        ? cart.items.find((item) => String(item.key) === String(data.key))
                        : null;
                    if (qtyDisplay) qtyDisplay.textContent = lineItem ? lineItem.quantity : (data?.quantity || 1);
                },
                onError: () => {
                    ButtonStateMachine.setError(el);
                    Toast.error('Could not add to cart');
                    NuclearUtils.removeClass(el, 'hidden');
                    NuclearUtils.removeClass(qtySelector, 'visible');
                }
            });
        });

        // Close Modal
        this.register('close-modal', () => {
            ModalManager.closeAll();
        });

        // PDP Add to Cart (with added state UI flip)
        this.register('pdp-add', (el) => {
            const variantId = el.dataset.variantId;
            if (!variantId) return;
            const cartOptions = typeof window.getSelectedPurchaseCartOptions === 'function'
                ? window.getSelectedPurchaseCartOptions()
                : {};

            // #96 Fix: Overselling Protection
            const max = parseInt(el.dataset.maxInventory, 10);
            if (!isNaN(max)) {
                const currentInCart = CartManager.getVariantQty(variantId);
                // Check if there is a linked qty input (default 1 on NaN)
                const qtyInput = el.closest('.pdp-actions')?.querySelector('input[type="number"]');
                const qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

                if (currentInCart + qty > max) {
                    Toast.error(`Only ${max} available!`);
                    return;
                }
            }

            const addState = el.querySelector('.mobile-btn-state-add');
            const addedState = el.querySelector('.mobile-btn-state-added');
            // Support both standard and mobile button structures
            const btnText = el.querySelector('.btn-text');

            ButtonStateMachine.setLoading(el);

            // Get quantity again for actual add (default 1 on NaN/empty)
            const qtyInput = el.closest('.pdp-actions')?.querySelector('input[type="number"]');
            const qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

            CartManager.addItem(variantId, qty, {
                ...cartOptions,
                onSuccess: () => {
                    if (addState) addState.style.display = 'none';
                    if (addedState) addedState.style.display = 'flex';
                    ButtonStateMachine.setSuccess(el);
                    Toast.success(window.nuclear.strings?.added || 'Added to cart!');

                    // Reset after delay
                    setTimeout(() => {
                        if (addState) addState.style.display = '';
                        if (addedState) addedState.style.display = 'none';
                        ButtonStateMachine.setIdle(el);
                    }, 3000);
                },
                onError: () => {
                    ButtonStateMachine.setError(el);
                    Toast.error('Could not add to cart');
                }
            });
        });

        // Form submit handler for notify
        const notifyForm = document.getElementById('notifyForm');
        if (notifyForm) {
            notifyForm.addEventListener('submit', (e) => {
                e.preventDefault();
                NotifyManager.submit(e);
            });
        }

        // Collection Add to Cart (with flip to qty selector)
        this.register('collection-add', (el) => {
            const variantId = el.dataset.variantId;
            if (!variantId) return;

            // #96 Fix: Overselling Protection
            const max = parseInt(el.dataset.maxInventory, 10);
            if (!isNaN(max)) {
                const currentInCart = CartManager.getVariantQty(variantId);
                if (currentInCart + 1 > max) {
                    Toast.error(`Only ${max} available!`);
                    return;
                }
            }

            // Generic wrapper lookup
            const wrapper = el.closest('[data-qty-wrapper]') || el.closest('.card-action-wrapper');
            const qtySelector = wrapper?.querySelector('.qty-selector') || wrapper?.querySelector('.qty-controls');
            const qtyDisplay = wrapper?.querySelector('[data-qty-display]'); // No change needed here if attribute used
            const qtyButtons = qtySelector?.querySelectorAll('[data-action="update-qty"], [data-action="collection-qty"]') || [];

            ButtonStateMachine.setLoading(el);
            CartManager.addItem(variantId, 1, {
                onSuccess: (data, cart) => {
                    ButtonStateMachine.setSuccess(el);
                    Toast.success(window.nuclear.strings?.added || 'Added to cart!');
                    if (wrapper && data && data.key) {
                        wrapper.dataset.lineKey = data.key;
                    }
                    qtyButtons.forEach((btn) => {
                        if (data && data.key) btn.dataset.lineKey = data.key;
                    });
                    const lineItem = data && data.key && cart && Array.isArray(cart.items)
                        ? cart.items.find((item) => String(item.key) === String(data.key))
                        : null;
                    // Flip to qty selector
                    if (qtySelector) {
                        el.style.display = 'none';
                        qtySelector.style.display = 'flex';
                        if (qtyDisplay) qtyDisplay.textContent = lineItem ? lineItem.quantity : (data?.quantity || 1);
                    }
                },
                onError: () => {
                    ButtonStateMachine.setError(el);
                    Toast.error('Could not add to cart');
                }
            });
        });

        // Update Cart Quantity (Debounced PER cart-line key).
        // A single shared debounce caused cross-row contamination: clicking
        // + on row A then + on row B within 300ms made row A's call get
        // cancelled — only row B's update fired and the cart silently
        // diverged from the UI. One debounce-timer per identifier fixes it.
        const _changeItemTimers = new Map();
        const debouncedChangeItem = (identifier, qty, opts) => {
            const key = String(identifier);
            if (_changeItemTimers.has(key)) clearTimeout(_changeItemTimers.get(key));
            const timer = setTimeout(() => {
                _changeItemTimers.delete(key);
                CartManager.changeItem(identifier, qty, opts);
            }, 300);
            _changeItemTimers.set(key, timer);
        };

        this.register('update-qty', (el) => {
            const identifier = el.dataset.lineKey || el.dataset.variantId;
            const change = parseInt(el.dataset.change, 10) || 0;
            const qtyEl = el.closest('[data-qty-wrapper]')?.querySelector('[data-qty-display]');

            if (!identifier || !qtyEl) return;

            // Optimistic Local Update
            const currentQty = parseInt(qtyEl.textContent, 10) || 0;
            const newQty = Math.max(0, currentQty + change);
            qtyEl.textContent = newQty;

            // Debounced API Call
            debouncedChangeItem(identifier, newQty, {
                onError: () => {
                    qtyEl.textContent = currentQty; // Revert on error
                }
            });
        });

        // Collection Qty Update (Debounced)
        this.register('collection-qty', (el) => {
            const identifier = el.dataset.lineKey || el.dataset.variantId;
            const change = parseInt(el.dataset.change, 10) || 0;
            const wrapper = el.closest('[data-qty-wrapper]');
            const qtyDisplay = wrapper?.querySelector('[data-qty-display]');
            const addBtn = el.closest('.card-action-wrapper')?.querySelector('.btn-card-add');

            if (!identifier || !qtyDisplay) return;

            // #118 Fix: Check if disabled
            if (el.disabled || el.classList.contains('disabled')) return;

            const currentQty = parseInt(qtyDisplay.textContent, 10) || 0;
            const newQty = Math.max(0, currentQty + change);

            // #96 Fix: Check max inventory
            const max = parseInt(el.dataset.maxInventory, 10);
            if (!isNaN(max) && newQty > max) {
                Toast.error(`Only ${max} available!`);
                return;
            }

            // Optimistic update
            qtyDisplay.textContent = newQty;

            if (newQty === 0) {
                debouncedChangeItem(identifier, 0, {
                    onSuccess: () => {
                        if (wrapper) wrapper.style.display = 'none';
                        if (addBtn) addBtn.style.display = 'block';
                        if (wrapper) wrapper.removeAttribute('data-line-key');
                        wrapper?.querySelectorAll('[data-line-key]').forEach((node) => node.removeAttribute('data-line-key'));
                    },
                    onError: () => {
                        qtyDisplay.textContent = currentQty;
                    }
                });
            } else {
                debouncedChangeItem(identifier, newQty, {
                    onError: () => {
                        qtyDisplay.textContent = currentQty;
                    }
                });
            }
        });

        // Homepage Hero CTA — premium single-pill that cross-fades between
        // ADD-TO-CART and an integrated [- qty +] · Checkout layout. The
        // pill gets `.is-active` when the current variant has any units
        // in the cart, which flips the layered states.
        const _setHomeCtaUI = (btn, qty) => {
            const wrapper = btn.closest('[data-home-cta]');
            const qtyDisplay = wrapper?.querySelector('[data-qty-display]');
            const activeState = wrapper?.querySelector('.home-cta__active');
            if (!wrapper) return;
            if (qty > 0) {
                wrapper.classList.add('is-active');
                if (activeState) activeState.setAttribute('aria-hidden', 'false');
                if (qtyDisplay) qtyDisplay.textContent = qty;
            } else {
                wrapper.classList.remove('is-active');
                if (activeState) activeState.setAttribute('aria-hidden', 'true');
                if (qtyDisplay) qtyDisplay.textContent = '1';
                wrapper.removeAttribute('data-line-key');
            }
        };

        // Exposed so the flavor switcher in nuclear-home.liquid can refresh
        // the pill state when the user picks a different flavor — the new
        // variant may already have a different qty in the cart.
        window.syncHomeSplitForVariant = (btn) => {
            const variantId = btn?.dataset?.variantId;
            if (!variantId) return;
            const qty = (window.CartManager && typeof CartManager.getVariantQty === 'function')
                ? CartManager.getVariantQty(variantId)
                : 0;
            _setHomeCtaUI(btn, qty);
        };

        this.register('home-add', (el) => {
            const variantId = el.dataset.variantId;
            if (!variantId) return;

            const max = parseInt(el.dataset.maxInventory, 10);
            if (!isNaN(max)) {
                const currentInCart = CartManager.getVariantQty(variantId);
                if (currentInCart + 1 > max) {
                    Toast.error(`Only ${max} available!`);
                    return;
                }
            }

            // Optimistic flip — pill morphs to active state immediately.
            // The cross-fade IS the success confirmation: no button color
            // flash, no "✓ Added" text swap, no success toast. The pill
            // already tells the user the item is in the cart.
            const optimisticQty = CartManager.getVariantQty(variantId) + 1;
            _setHomeCtaUI(el, optimisticQty);

            CartManager.addItem(variantId, 1, {
                onSuccess: (data) => {
                    const wrapper = el.closest('[data-home-cta]');
                    if (wrapper && data && data.key) wrapper.dataset.lineKey = data.key;
                },
                onError: () => {
                    // Roll back the optimistic flip.
                    _setHomeCtaUI(el, optimisticQty - 1);
                    Toast.error('Could not add to cart');
                }
            });
        });

        this.register('home-qty', (el) => {
            const change = parseInt(el.dataset.change, 10) || 0;
            const wrapper = el.closest('[data-home-cta]');
            const btn = wrapper?.querySelector('[data-action="home-add"]');
            const qtyDisplay = wrapper?.querySelector('[data-qty-display]');
            if (!wrapper || !btn || !qtyDisplay) return;

            const variantId = btn.dataset.variantId;
            const identifier = wrapper.dataset.lineKey || variantId;
            const currentQty = parseInt(qtyDisplay.textContent, 10) || 0;
            const newQty = Math.max(0, currentQty + change);

            const max = parseInt(btn.dataset.maxInventory, 10);
            if (change > 0 && !isNaN(max) && newQty > max) {
                Toast.error(`Only ${max} available!`);
                return;
            }

            // Optimistic update first so the UI feels instant; cart:updated
            // will reconcile to server truth.
            _setHomeCtaUI(btn, newQty);

            debouncedChangeItem(identifier, newQty, {
                onError: () => {
                    _setHomeCtaUI(btn, currentQty);
                }
            });
        });

        // toggle-cart: always navigate to /cart. The drawer feature was
        // removed (cart-drawer snippet deleted); the drawerEl lookup
        // branch is gone with it.
        this.register('toggle-cart', () => {
            window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_url)
                || window.NUCLEAR_CONFIG.ROUTES.cart;
        });

        // Sticky Drawer Actions
        this.register('close-drawer', () => {
            if (window.ModalManager && window.ModalManager.active) {
                ModalManager.closeAll();
                return;
            }

            if (typeof collapseDrawer === 'function' && window.drawerExpanded) {
                collapseDrawer();
            }
        });

        this.register('notify-modal', (el) => {
            const pid = el.dataset.productId;
            const title = el.dataset.productTitle;
            if (window.NotifyManager) NotifyManager.openModal(pid, title);
        });

        // #140 Fix: Mobile Menu Actions
        this.register('toggle-menu', () => {
            // Register with ModalManager on fly if needed, or simple toggle
            const menu = document.getElementById('MobileMenu');
            if (!menu) return;

            // Use ModalManager if possible for backdrop handling
            if (window.ModalManager) {
                // Register if not already
                if (!window.ModalManager.modals['mobile-menu']) {
                    window.ModalManager.register('mobile-menu', { element: '#MobileMenu', backdrop: false });
                }
                window.ModalManager.open('mobile-menu');
            } else {
                // Fallback
                menu.classList.add('visible');
                document.body.style.overflow = 'hidden';
            }
        });

        this.register('close-menu', () => {
            if (window.ModalManager) {
                window.ModalManager.close('mobile-menu');
            } else {
                const menu = document.getElementById('MobileMenu');
                if (menu) menu.classList.remove('visible');
                document.body.style.overflow = '';
            }
        });

        this.register('sticky-qty-update', (el) => {
            if (typeof window.updateMobileQty === 'function') {
                window.updateMobileQty(parseInt(el.dataset.change, 10) || 0);
                return;
            }

            const change = parseInt(el.dataset.change, 10) || 0;
            // Sync with the currently selected variant, not the last optimistic global
            const selector = document.getElementById('VariantSelector')
                || document.getElementById('VariantSelectorDesktop')
                || document.querySelector('[data-action="select-variant"]');
            const variantId = selector ? selector.value : el.closest('[data-default-variant-id]')?.dataset.defaultVariantId;

            if (!variantId) return;

            const currentQty = typeof window.getPdpVariantQty === 'function'
                ? window.getPdpVariantQty(variantId)
                : (window.stickyCartQty || window.mobileCartQty || 1);
            let newQty = Math.max(0, currentQty + change);

            // Optimistic UI
            if (typeof window.syncButtons === 'function') window.syncButtons(newQty, variantId);

            if (newQty === 0) {
                CartManager.changeItem(variantId, 0, {
                    onError: () => {
                        if (typeof window.syncButtons === 'function') window.syncButtons(currentQty, variantId);
                    }
                });
            } else {
                CartManager.changeItem(variantId, newQty, {
                    onError: () => {
                        if (typeof window.syncButtons === 'function') window.syncButtons(currentQty, variantId);
                    }
                });
            }
        });
    }
};

window.ActionHandler = ActionHandler;

// 9. CORE CONTROLLER
class NuclearController {
    constructor() {
        if (NuclearController.instance) {
            return NuclearController.instance;
        }
        NuclearController.instance = this;

        this.events = new EventBus();
        this.utils = {
            Toast,
            CartToast,
            ModalManager,
            WishlistManager,
            CartManager,
            NotifyManager,
            ButtonStateMachine,
            ActionHandler,
            ...NuclearUtils
        };

        // Legacy/Global Attachments
        window.Toast = Toast;
        window.CartToast = CartToast;
        window.CartManager = CartManager;
        window.WishlistManager = WishlistManager;
        window.ModalManager = ModalManager;
        window.NotifyManager = NotifyManager;

        this.init();
    }

    destroy() {
        // Cleanup listeners if improved to store them
        // For now, minimal cleanup as per current architecture
        if (this.headerScrollHandler) {
            window.removeEventListener('scroll', this.headerScrollHandler);
        }
    }

    init() {
        if (this.initialized) return;
        this.initialized = true;

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.onReady());
        } else {
            this.onReady();
        }
    }

    // #36 Fix: SPA Re-initialization
    reinit() {
        if (!this.initialized) return;
        this.destroy(); // Cleanup old listeners

        // Re-run standard init flow
        this.initHeader();
        this.initTabs();
        this.initLivePulse();
        // #37 Fix: Event Delegation
        // Note: Global click delegation is handled in ActionHandler without blocking analytics
        // #31 Fix: Video Carousel
        if (this.initWaysCarousel) this.initWaysCarousel();

        // Sync subsystems that need UI refresh
        if (this.utils.WishlistManager) this.utils.WishlistManager.syncUI();
        if (this.utils.CartManager) this.utils.CartManager.sync();
    }

    onReady() {
        // Init Subsystems
        Toast.init();
        ModalManager.init();
        // (cart-drawer modal removed — snippet deleted; nav now uses
        // window.location.href = routes.cart_url instead.)
        WishlistManager.init();
        NotifyManager.init();
        ActionHandler.init();

        // Cart Sync
        CartManager.sync();

        // Initial sync of any home-page split buttons in case the user
        // arrived with items already in the cart (e.g. revisiting the
        // store with cart cookie intact).
        document.querySelectorAll('[data-action="home-add"]').forEach((btn) => {
            if (typeof window.syncHomeSplitForVariant === 'function') {
                window.syncHomeSplitForVariant(btn);
            }
        });

        // Keep home split buttons in sync with cart changes coming from
        // anywhere else (drawer, /cart page, another tab via storage event).
        if (!window._homeSplitListenerBound) {
            window._homeSplitListenerBound = true;
            window.addEventListener('cart:updated', () => {
                document.querySelectorAll('[data-action="home-add"]').forEach((btn) => {
                    if (typeof window.syncHomeSplitForVariant === 'function') {
                        window.syncHomeSplitForVariant(btn);
                    }
                });
            });
        }

        // Audio System
        this.initAudio();

        // Live Pulse
        this.initLivePulse();

        // Product Tabs
        this.initTabs();

        // Sticky Header
        this.initHeader();

        // #31 Fix: Video Carousel
        this.initWaysCarousel();
    }

    /* === HEADER (Fixes #68 Sticky Glitches) === */
    /* NOTE: Scroll handler REMOVED to prevent duplicate with header.liquid.
       The header.liquid implementation uses requestAnimationFrame and is more comprehensive.
       Having two handlers was causing the "scroll bounce-back" issue. */
    initHeader() {
        // INTENTIONALLY EMPTY - scroll logic is in header.liquid
    }

    /* === TABS (Fixes #67 CLS) === */
    initTabs() {
        const containers = document.querySelectorAll('.product-tabs-section');

        // #67 Fix: Set min-height to prevent layout shifts
        containers.forEach(container => {
            const panels = container.querySelectorAll('.tab-panel');
            let maxH = 0;
            // Calculate max height of all panels to reserve space? 
            // Or just let it flow but animate smoothly. 
            // For now, let's just ensure the click doesn't jump.
        });

        document.querySelectorAll('.tab-btn[data-tab]').forEach(btn => {
            btn.addEventListener('click', () => {
                const tabId = btn.dataset.tab;
                const container = btn.closest('.product-tabs-section');
                if (!container) return;

                // Remove active from all buttons
                container.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
                // Add active to clicked button
                btn.classList.add('active');

                // Hide all panels
                container.querySelectorAll('.tab-panel').forEach(p => p.classList.remove('active'));
                // Show target panel
                const panel = container.querySelector(`#tab-${tabId}`);
                if (panel) {
                    panel.classList.add('active');
                    // #67 Animation reset
                    panel.style.animation = 'none';
                    panel.offsetHeight; /* trigger reflow */
                    panel.style.animation = 'fadeIn 0.5s ease';
                }
            });
        });
    }

    /* === AUDIO ===
       Disabled. The source files at /assets/sounds/*.mp3 are not in the
       theme's /assets directory, so every product page load was emitting
       two 404 requests. The mouseover handler was also unthrottled and
       played a sound on every hover near any button — UX nightmare.

       We still expose an empty window.nuclear.audio namespace so consumers
       (nuclear-product.js, nuclear-specialty.js) that defensively check
       `window.nuclear.audio.click` simply see undefined and no-op. */
    initAudio() {
        if (!window.nuclear) return;
        if (!window.nuclear.audio) window.nuclear.audio = {};
    }

    /* === LIVE PULSE === Removed: the `live-pulse` snippet has been
       deleted. The interval-cleanup hook in destroy() is also gone. */
    initLivePulse() { /* no-op */ }

    /* === WAYS TO ENJOY - World-Class Video Carousel === */
    initWaysCarousel() {
        // #31 Fix: Duplicate IDs - Use Class Based Selection
        const track = document.querySelector('.ways-track');
        if (!track) return; // Exit if not found

        // Disconnect any observer from a previous init so we don't leak one
        // (plus its observed nodes) each time reinit() runs.
        if (this._waysObserver) this._waysObserver.disconnect();

        // Check for unique ID or use generic class if reused
        // If reused, we might need multiple instances, but for now we assume one ways section

        const cards = track.querySelectorAll('.way-card');
        const videos = track.querySelectorAll('video');

        // Observer for auto-play/pause
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const vid = entry.target.querySelector('video');
                if (!vid) return;

                if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                    vid.play().catch(() => { });
                    entry.target.classList.add('active');
                } else {
                    vid.pause();
                    entry.target.classList.remove('active');
                }
            });
        }, {
            threshold: 0.6
        });

        this._waysObserver = observer;
        cards.forEach(card => observer.observe(card));
    }
}

// Instantiate Global — preserve properties set by theme.liquid (strings) and the
// pre-constructor EventBus (line 173) so earlier subscribers and i18n strings survive.
const _preservedNuclear = {
    strings: window.nuclear && window.nuclear.strings,
    events: window.nuclear && window.nuclear.events
};
window.nuclear = new NuclearController();
if (_preservedNuclear.strings) window.nuclear.strings = _preservedNuclear.strings;
if (_preservedNuclear.events) window.nuclear.events = _preservedNuclear.events;

/* ========================================
   9. SALES TICKER BRAIN (Indestructible)
   ======================================== */
// Field names match the JSON shape produced by templates/index.order-feed.liquid.
// (discount_val + product — NOT saved + item, which is what the old templates
// referenced. That mismatch made the ticker render "saved undefined on undefined".)
const TickerTemplates = {
    discount: (o) => `🔥 <strong>${o.name}</strong> (${o.city}) saved <strong>${o.discount_val || ''}</strong> on ${o.product || ''}`,
    volume:   (o) => `📦 <strong>${o.name}</strong> just stocked up with a <strong>${o.product || ''}</strong>!`,
    shipping: (o) => `🚚 <strong>${o.name}</strong> (${o.city}) just unlocked <strong>Free Priority Shipping</strong>`,
    standard: (o) => `⚡ <strong>${o.name}</strong> just bought <strong>${o.product || ''}</strong> in ${o.city}`
};

const SYSTEM_MESSAGES = [
    `🔥 Stocks running low on <strong>Spicy Mix</strong>`,
    `👀 <strong>420 people</strong> viewing right now`,
    `⚡ Operations normal. Shipping fast to <strong>you</strong>.`
];

class SalesTickerManager {
    constructor() {
        this.orders = [];
        // Monotonic message counter. Was `this.queue = []` — an array that
        // was push()'d on every ticker message and never drained, growing
        // unbounded on the long-lived home page. Only its length was ever
        // read (for the every-5th-message cadence), so a counter suffices.
        this.msgCount = 0;
        this.cacheKey = 'nuclear_ticker_v1';
        this.cacheDuration = 1000 * 60 * 5; // 5 minutes
        this.isPlaying = false;
        this.streakTracker = { lastType: null };
    }

    async init() {
        // 1. Try Cache
        const cached = this.getFromCache();
        if (cached) {
            this.orders = cached;
        } else {
            // 2. Fetch Fresh
            await this.fetchData();
        }

        // 3. Start The Radio Station
        this.isPlaying = true;
        this.broadcastLoop();
    }

    getFromCache() {
        try {
            const raw = localStorage.getItem(this.cacheKey);
            if (!raw) return null;
            const data = JSON.parse(raw);
            if (Date.now() - data.timestamp > this.cacheDuration) return null;
            return data.orders;
        } catch (e) { return null; }
    }

    async fetchData() {
        try {
            // Fetch from Shadow Endpoint
            const res = await fetch('/?view=order-feed');
            if (!res.ok) throw new Error('Endpoint failed');

            const text = await res.text();
            // Parse JSON (Liquid might output whitespace, so trim)
            let json = JSON.parse(text.trim());

            // Validate
            if (!Array.isArray(json) || json.length === 0) throw new Error('Empty data');

            this.orders = json;

            // Save to Cache
            localStorage.setItem(this.cacheKey, JSON.stringify({
                timestamp: Date.now(),
                orders: this.orders
            }));

        } catch (e) {
            console.warn('?? Ticker: Network failed, engaging FALLBACK protocol.', e);
            this.orders = this.getFallbackData();
        }
    }

    getFallbackData() {
        // Bulletproof Backup
        return [
            { name: 'Alex', city: 'New York', product: 'Garlic Gold', template: 'standard' },
            { name: 'Sarah', city: 'London', product: 'Spicy Mix', template: 'discount', discount_val: '10%' },
            { name: 'Mike', city: 'Chicago', product: 'Master Bundle', template: 'volume', item_count: '3-Pack' },
            { name: 'Emily', city: 'Austin', product: 'Truffle Dust', template: 'shipping' },
            { name: 'David', city: 'Toronto', product: 'Chilli Oil', template: 'standard' }
        ];
    }

    async broadcastLoop() {
        if (!this.isPlaying) return;

        // 1. Generate Next Message
        const messageHtml = this.getNextMessage();

        // 2. Broadcast Event (The Radio Signal)
        window.nuclear.events.emit('TICKER_UPDATE', messageHtml);

        // 3. Loop (4 seconds)
        setTimeout(() => this.broadcastLoop(), 4000);
    }

    getNextMessage() {
        // Hype Injector: Every 5th message is a System Message
        if (this.msgCount % 5 === 4) {
            this.msgCount++;
            return SYSTEM_MESSAGES[Math.floor(Math.random() * SYSTEM_MESSAGES.length)];
        }

        // Pick Random Order
        const order = this.orders[Math.floor(Math.random() * this.orders.length)];

        // Anti-Streak Logic
        // If we just showed a 'shipping' message, try to pick another type
        if (order.template === this.streakTracker.lastType) {
            // Try one more distinct pick
            const altOrder = this.orders[Math.floor(Math.random() * this.orders.length)];
            if (altOrder.template !== this.streakTracker.lastType) {
                return this.formatOrder(altOrder);
            }
        }

        return this.formatOrder(order);
    }

    formatOrder(order) {
        this.streakTracker.lastType = order.template || 'standard';
        this.msgCount++;

        const templateFn = TickerTemplates[order.template] || TickerTemplates.standard;
        return templateFn(order);
    }
}

// Attach to Global System
window.nuclear.ticker = new SalesTickerManager();
document.addEventListener('DOMContentLoaded', () => {
    // Start slightly delayed to let main thread breathe
    setTimeout(() => window.nuclear.ticker.init(), 1000);
});

// ============================================
// NUCLEAR SCROLL PHYSICS SYSTEM
// Premium scroll behaviors for world-class UX
// ============================================

(function () {
    'use strict';

    // Holds the previously-created IntersectionObserver so we can disconnect
    // it before creating a new one on SPA navigation. Without this, every
    // navigation leaked another observer attached to detached DOM nodes.
    let staggerObserver = null;

    const initScrollPhysics = () => {
        // Disconnect previous observer (SPA nav re-runs this function).
        if (staggerObserver) {
            try { staggerObserver.disconnect(); } catch (_) {}
            staggerObserver = null;
        }

        const staggerElements = Array.from(document.querySelectorAll('.stagger-card, .ingredient-item')).filter((el) => {
            return !el.closest('.product-rail-section');
        });

        if (staggerElements.length > 0 && 'IntersectionObserver' in window) {
            staggerElements.forEach((el, index) => {
                el.style.opacity = '0';
                el.style.transform = 'translateY(40px)';
                el.style.transition = `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`;
            });

            staggerObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.style.opacity = '1';
                        entry.target.style.transform = 'translateY(0)';
                        const cleanup = () => {
                            entry.target.style.transition = '';
                            entry.target.removeEventListener('transitionend', cleanup);
                        };
                        entry.target.addEventListener('transitionend', cleanup);
                        staggerObserver.unobserve(entry.target);
                    }
                });
            }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

            staggerElements.forEach(el => staggerObserver.observe(el));
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            setTimeout(initScrollPhysics, 100);
        });
    } else {
        setTimeout(initScrollPhysics, 100);
    }

    window.addEventListener('pageTransitionComplete', () => {
        setTimeout(initScrollPhysics, 100);
    });
})();
